import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { Sliders, Settings, ThumbsUp } from "lucide-react";
import Sidebar from "../../components/Sidebar";
import PageTopBar from "../../components/PageTopBar";
import { useAuth } from "../../lib/auth";

/**
 * Admin area shell.
 *
 * Super_admin sees both sub-pages (models + general config). Regular
 * `admin` role only sees the general config since the AI-model catalog
 * is a platform-wide concern reserved for super_admin (business decision).
 */
export default function AdminLayout() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const location = useLocation();
    // Redirect admin away from /admin/modelos
    if (!isSuper && location.pathname.startsWith("/admin/modelos")) {
        return <Navigate to="/admin/configuracion" replace />;
    }
    return (
        <div className="flex min-h-screen bg-ink-950">
            <Sidebar />
            <main className="flex-1 overflow-y-auto">
                <PageTopBar />
                <div className="max-w-6xl mx-auto px-10 py-14 space-y-10">
                    <div>
                        <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-2">
                            Panel de administración
                        </p>
                        <h1 className="font-display text-5xl text-fog-100">Admin</h1>
                        <p className="text-fog-400 text-sm mt-2 max-w-xl">
                            {isSuper
                                ? "Configura los modelos de IA y la plataforma desde una misma consola. Elige la sección abajo."
                                : "Administra el presupuesto, usuarios y almacenamiento de tu plataforma."}
                        </p>
                    </div>

                    <nav
                        data-testid="admin-subnav"
                        className="flex items-center gap-2 border-b hairline border-ink-600 -mt-4"
                    >
                        {isSuper && (
                            <SubTab
                                to="/admin/modelos"
                                icon={Sliders}
                                label="Datos y comportamiento de los modelos"
                                testId="admin-subnav-models"
                            />
                        )}
                        <SubTab
                            to="/admin/configuracion"
                            icon={Settings}
                            label="Configuración general"
                            testId="admin-subnav-general"
                        />
                        <SubTab
                            to="/admin/valoraciones"
                            icon={ThumbsUp}
                            label="Valoraciones"
                            testId="admin-subnav-ratings"
                        />
                    </nav>

                    <Outlet />
                </div>
            </main>
        </div>
    );
}

function SubTab({ to, icon: Icon, label, testId }) {
    return (
        <NavLink
            to={to}
            data-testid={testId}
            className={({ isActive }) =>
                `relative inline-flex items-center gap-2 px-4 py-3 text-sm transition-colors ${
                    isActive
                        ? "text-periwinkle-500"
                        : "text-fog-400 hover:text-fog-100"
                }`
            }
        >
            {({ isActive }) => (
                <>
                    <Icon className="w-4 h-4" />
                    <span>{label}</span>
                    {isActive && (
                        <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-periwinkle-500 rounded-full" />
                    )}
                </>
            )}
        </NavLink>
    );
}
